import React from 'react';
import { HomeIcon, ArrowPathIcon } from '@heroicons/react/24/outline';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console or error reporting service
    console.error('Error caught by boundary:', error, errorInfo);
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleRefresh = () => {
    // Reset the error boundary state
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Reload the page
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="text-center">
              {/* Logo */}
              <img
                className="mx-auto h-24 w-auto mb-8"
                src="/assets/icons/nazai-studio-logo.png"
                alt="Nazaï Studio"
              />
              
              {/* Error Message */}
              <div className="mb-8">
                <h1 className="text-6xl font-bold text-red-600">Oops!</h1>
                <h2 className="text-3xl font-bold text-gray-900 mt-4">Une erreur s'est produite</h2>
                <p className="text-lg text-gray-600 mt-4">
                  Quelque chose s'est mal passé. Nous nous excusons pour la gêne occasionnée.
                </p>
              </div>

              {/* Action Buttons */}
              <div className="space-y-4">
                <button
                  onClick={this.handleRefresh}
                  className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                >
                  <ArrowPathIcon className="h-5 w-5 mr-2" />
                  Recharger la page
                </button>
                
                <div className="block">
                  <a
                    href="/"
                    className="inline-flex items-center px-6 py-3 border border-gray-300 text-base font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 transition-colors duration-200"
                  >
                    <HomeIcon className="h-5 w-5 mr-2" />
                    Retour à l'accueil
                  </a>
                </div>
              </div>

              {/* Error Details (only in development) */}
              {process.env.NODE_ENV === 'development' && this.state.error && (
                <div className="mt-8 pt-8 border-t border-gray-200">
                  <details className="text-left">
                    <summary className="text-sm text-gray-500 cursor-pointer mb-4">
                      Détails de l'erreur (développement)
                    </summary>
                    <div className="bg-gray-100 p-4 rounded-md text-xs text-gray-700 overflow-auto max-h-40">
                      <div className="font-bold mb-2">Erreur:</div>
                      <div className="mb-4">{this.state.error.toString()}</div>
                      <div className="font-bold mb-2">Stack trace:</div>
                      <pre className="whitespace-pre-wrap">{this.state.errorInfo.componentStack}</pre>
                    </div>
                  </details>
                </div>
              )}

              {/* Contact Info */}
              <div className="mt-8 pt-8 border-t border-gray-200">
                <p className="text-sm text-gray-500 mb-4">
                  Si le problème persiste, contactez-nous :
                </p>
                <a
                  href="mailto:hello@nazaistudio.ma"
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                >
                  hello@nazaistudio.ma
                </a>
              </div>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
